import { message } from 'antd';
import { isCancel } from 'axios';
import { useReducer, useEffect } from 'react';
import { instance } from 'store/utils';
import { Errors } from 'utils/errors';

/**
 * @description Handles the **account information retrieval** used to populate the confirm registration form.
 * @returns {{
 *   data: object | null,
 *   isFetching: boolean,
 *   isError: boolean,
 *   error: unknown | null
 * }} The query state containing the fetched data, loading flag, and error (if any).
 */
export function useConfirmRegistrationQuery() {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchUser() {
      dispatch({ type: 'QUERY_START' });

      try {
        const res = await instance.get(
          `/onboarding/funding-application/account`,
          {
            signal: controller.signal,
          }
        );
        dispatch({ type: 'QUERY_SUCCESS', payload: res.data });
      } catch (err) {
        if (isCancel(err)) {
          dispatch({ type: 'QUERY_CANCELLED' });
          return;
        }
        const errorMessage = Errors.GenericRetrievalError;
        message.error(errorMessage);
        dispatch({ type: 'QUERY_ERROR', payload: err });
      }
    }

    fetchUser();
    return () => controller.abort();
  }, []);

  return state;
}

const initialState = {
  data: null,
  isFetching: false,
  isError: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'QUERY_START':
      return { data: null, isFetching: true, isError: false, error: null };
    case 'QUERY_SUCCESS':
      return {
        data: action.payload,
        isFetching: false,
        isError: false,
        error: null,
      };
    case 'QUERY_ERROR':
      return {
        data: null,
        isFetching: false,
        isError: true,
        error: action.payload,
      };
    case 'QUERY_CANCELLED':
      return { ...state, isFetching: false };
    default:
      return state;
  }
}
